import React from "react";
import { useParams } from "react-router-dom";
import {
  PublicProvider,
  providerKindLabels,
  readPublicProvider,
} from "services/provider-profile.service";

/**
 * A provider's public page. Anyone can open it, signed in or not; an unlisted provider
 * is simply not found, so nothing private shows here.
 */
const ProviderPublicPage = () => {
  const { orgId } = useParams();
  const id = Number(orgId);
  const [provider, setProvider] = React.useState<PublicProvider | null>(null);
  const [state, setState] = React.useState<"loading" | "found" | "missing">("loading");

  React.useEffect(() => {
    let cancelled = false;
    if (!Number.isFinite(id) || id <= 0) {
      setState("missing");
      return;
    }
    (async () => {
      const { data, error } = await readPublicProvider(id);
      if (cancelled) return;
      if (error || !data) setState("missing");
      else {
        setProvider(data);
        setState("found");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <main className="min-h-screen bg-stone-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <a href="/" className="text-lg font-semibold text-slate-800">
            Funely
          </a>
        </div>
      </header>
      <section className="mx-auto max-w-3xl px-6 py-12">
        {state === "loading" && <p className="text-slate-500">Loading…</p>}
        {state === "missing" && (
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Provider not found</h1>
            <p className="text-slate-600">
              This provider is not listed on Funely, or the link is not right.
            </p>
          </div>
        )}
        {state === "found" && provider && (
          <article>
            <p className="text-sm font-medium tracking-wide text-slate-500 uppercase mb-2">
              {providerKindLabels(provider).join(" · ")}
            </p>
            <h1 className="text-3xl font-semibold text-slate-900 mb-6">{provider.name}</h1>
            {provider.publicDescription ? (
              <p className="whitespace-pre-line text-slate-700 leading-relaxed">
                {provider.publicDescription}
              </p>
            ) : (
              <p className="text-slate-500">This provider has not added a description yet.</p>
            )}
            {provider.latitude != null && provider.longitude != null && (
              <p className="mt-8">
                <a
                  className="text-sm underline text-slate-700"
                  href={`https://www.openstreetmap.org/?mlat=${provider.latitude}&mlon=${provider.longitude}#map=15/${provider.latitude}/${provider.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  See where they are on a map
                </a>
              </p>
            )}
          </article>
        )}
      </section>
    </main>
  );
};

export default ProviderPublicPage;
